import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDesignerScreenContext } from '../../components/Chat/DesignerScreenContext';
import { getSelectedComponentArtifactContext } from './Utils/ReactComponentSelectionUtils';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { buildAndPublishReactCode } from './Utils/buildAndPublishReactCode';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatIcon from '@mui/icons-material/Chat';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FolderIcon from '@mui/icons-material/Folder';
import LabelIcon from '@mui/icons-material/Label';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import SendIcon from '@mui/icons-material/Send';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, ButtonGroup, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Popover, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';

import { useApiClient, useCopyToClipboard, useIsMobile, usePersistedSetting, useToastService } from '@@common';
import { ConfirmDialog, ViewTitleBar } from '@@components';

import { ApiException, ComponentMetadataModel, ReactCodeLifecycleState, ReactCodeStatusModel, UserInterfaceClient } from '../../api/api';
import { getValidationErrorMessage, parseValidationError } from '../../api/ApiValidationUtils';
import { useSearchParamsEx } from '../../common/hooks/useSearchParamsEx';
import { serializeWithTypeFirst } from '../../common/serializeWithTypeFirst';
import { useChangeSet } from '../../components/ChangeSetContext';
import { CodeFileViewer } from '../../components/CodeFileViewer';
import { useNotifications } from '../../components/NotificationContext';
import SaveErrorDialog, { SaveError } from '../../components/SaveErrorDialog';
import { ViewLoader } from '../FlowDesigner/Components/ViewLoader';
import { ActionPanel } from './Components/ActionPanel';
import {
	AppArtifactPanel,
	isGeneratedReactSourceFile,
	ITEM_TYPE_EMBEDDED_RESOURCE,
	ITEM_TYPE_REACT_SOURCE_FILE,
	ITEM_TYPE_SETTINGS,
	ITEM_TYPE_SHARED_COMPONENT,
	ITEM_TYPE_THEME,
	ITEM_TYPE_VIEW,
	SETTINGS_ITEM_BUNDLE_SETTINGS,
	type ReactSourceFileArtifact,
	type SelectedItem,
	SharedComponentSelectionContext,
} from './Components/AppArtifactPanel';
import { AppResourceDesigner } from './Components/AppResourceDesigner';
import { DesignerPresenceIndicator, viewersForItem } from './Components/DesignerPresenceIndicator';
import { RuntimePreviewFrame } from './Components/RuntimePreviewFrame';
import RestoreAppVersionDialog from './Components/RestoreAppVersionDialog';
import { ThemeDesigner } from './Components/ThemeDesigner';
import DesignerAppProvider from './Contexts/DesignerAppContext';
import DesignerViewProvider from './Contexts/DesignerViewContext';
import UserInterfaceMetadataProvider from './Contexts/UserInterfaceMetadataContext';
import { useAppDesignerPresence } from './Hooks/useAppDesignerPresence';
import useUserInterfaceMetadata from './Hooks/useUserInterfaceMetadata';
import { Bundle, withDefaultBundleSettings } from './Models/Bundle';
import { EmbeddedResourceModel } from './Models/EmbeddedResourceModel';
import { IComponentModel } from './Models/IComponentModel';
import { IContainerComponentModel } from './Models/IContainerComponentModel';
import { ReactBundleModel } from './Models/ReactBundleModel';
import { ViewModel } from './Models/ViewModel';
import { SHARED_COMPONENT_ROOT_TYPE } from './Types/DesignerMessages';
import {
	ComponentPath,
	addComponentAtPath,
	createDefaultComponent,
	findClosestContainerPath,
	moveComponentDown,
	moveComponentUp,
	removeComponentAtPath,
	resolveSharedComponentSelection,
	updateComponentAtPath,
} from './Utils/ComponentTreeUtils';
import { getComponentTreePath } from './Utils/VirtualComponentCollectionUtils';
import { parseVersionTags } from './Utils/versionTags';
import { getInitialDesignerView, hasRouteParameters } from './Utils/initialDesignerView';

const SELECTED_ITEM_STORAGE_KEY = 'bundleDesigner.selectedItem';

type AppDesignerViewUrlParams = {
	definitionId: string;
	versionId?: string;
};

/**
 * Find an item by ID across all types in the bundle.
 * Returns the first match found or null if not found.
 */
function findItemById(bundle: Bundle | null, reactSourceFileIds: ReadonlySet<string>, id: string): SelectedItem | null {
	if (!bundle) return null;

	// Search in Views
	if (bundle.Views.some((v) => v.Id === id)) {
		return { type: ITEM_TYPE_VIEW, id };
	}

	// Search in SharedComponents
	if (bundle.SharedComponents.some((c) => c.Id === id)) {
		return { type: ITEM_TYPE_SHARED_COMPONENT, id };
	}

	// Search in EmbeddedResources
	if (bundle.EmbeddedResources.some((r) => r.Id === id)) {
		return { type: ITEM_TYPE_EMBEDDED_RESOURCE, id };
	}

	if (bundle.Themes?.some((theme) => theme.Id === id)) {
		return { type: ITEM_TYPE_THEME, id };
	}

	if (id === SETTINGS_ITEM_BUNDLE_SETTINGS || id === 'phoneNumberFormat' || id === 'currencyCode') {
		return { type: ITEM_TYPE_SETTINGS, id: SETTINGS_ITEM_BUNDLE_SETTINGS };
	}

	if (reactSourceFileIds.has(id)) {
		return { type: ITEM_TYPE_REACT_SOURCE_FILE, id };
	}

	return null;
}

function isViewDefinition(value: unknown): value is ViewModel {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return false;
	}

	const candidate = value as Record<string, unknown>;
	return typeof candidate.Type === 'string' && Array.isArray(candidate.Children) && candidate.Layout !== null && typeof candidate.Layout === 'object';
}

function AppDesignerContent() {
	const { definitionId, versionId } = useParams<AppDesignerViewUrlParams>();
	const isSnapshotView = Boolean(versionId);
	const navigate = useNavigate();
	const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
	const isMobile = useIsMobile();
	const [mobileDesignerSection, setMobileDesignerSection] = useState<'artifacts' | 'preview' | 'properties' | 'chat'>('artifacts');
	const [searchParams, setSearchParam] = useSearchParamsEx();
	const selectedItemFromQuery = searchParams.get('selectedItem');

	const client = useApiClient(UserInterfaceClient);
	const queryClient = useQueryClient();
	const toastService = useToastService();
	const copyToClipboard = useCopyToClipboard();
	const { latestReactBuildEvent, subscribeToReactBuilds } = useNotifications();

	const { componentByName } = useUserInterfaceMetadata();

	const { currentChangeSetId } = useChangeSet();
	const bundleQueryKey = useMemo(
		() => isSnapshotView ? ['userinterface', 'bundle-version', versionId] as const : ['userinterface', 'bundle', definitionId, currentChangeSetId ?? ''] as const,
		[definitionId, currentChangeSetId, isSnapshotView, versionId],
	);

	const { data: record, isLoading } = useQuery({
		queryKey: bundleQueryKey,
		queryFn: () => isSnapshotView ? client.getById(versionId!) : client.getByDefinitionId(definitionId!, currentChangeSetId!),
		enabled: !!definitionId && (!isSnapshotView || !!versionId),
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const [bundleName, setBundleName] = useState<string>('');
	const [bundleTags, setBundleTags] = useState<string[]>([]);
	const [versionTagsText, setVersionTagsText] = useState('');
	const [versionTagsDialogOpen, setVersionTagsDialogOpen] = useState(false);
	const [bundle, setBundle] = useState<Bundle | null>(null);
	const [hasBundleChanges, setHasBundleChanges] = useState(false);
	const [hasSourceChanges, setHasSourceChanges] = useState(false);
	const [savePending, setSavePending] = useState(false);
	const [saveError, setSaveError] = useState<SaveError>();

	const handleBundleChanged = useCallback((updated: Bundle | null) => {
		setBundle(updated);
		setHasBundleChanges(true);
	}, []);

	// Ensure bundle.Name is always synchronized with bundleName
	const bundleWithName = useMemo(() => {
		return bundle ? { ...bundle, Name: bundleName } : null;
	}, [bundle, bundleName]);

	// Note: Initial state will be resolved from query params after bundle loads
	const [storedSelectedItem, setSelectedItem] = usePersistedSetting<SelectedItem | null>(SELECTED_ITEM_STORAGE_KEY, null);
	const [initialSelectionResolved, setInitialSelectionResolved] = useState(false);
	const initialSelectedItemIdRef = useRef(selectedItemFromQuery);
	const selectedItem = useMemo<SelectedItem | null>(() => {
		if (!initialSelectionResolved && bundle && storedSelectedItem?.type === ITEM_TYPE_VIEW) {
			const rememberedView = bundle.Views.find((view) => view.Id === storedSelectedItem.id);
			if (hasRouteParameters(rememberedView?.RoutePath)) {
				const initialView = getInitialDesignerView(bundle.Views, rememberedView?.Id);
				return initialView ? { type: ITEM_TYPE_VIEW, id: initialView.Id } : null;
			}
		}
		return storedSelectedItem;
	}, [bundle, initialSelectionResolved, storedSelectedItem]);

	const snapshotSelectionAppliedRef = useRef<string | null>(null);

	const [selectedComponentPath, setSelectedComponentPath] = useState<ComponentPath | null>(null);

	// Track when selection is inside a SharedComponent (via ReferenceComponent)
	// This helps AppItemProperties resolve the component correctly
	const [selectedSharedComponentContext, setSelectedSharedComponentContext] = useState<SharedComponentSelectionContext | null>(null);

	// Incremented only when the user explicitly selects a view/shared component in Artifacts.
	// RuntimePreviewFrame fires the navigation effect only on this counter, so passive view
	// syncs (VIEW_ACTIVATED, component clicks) never cause a redundant navigation message.
	const [navRequestId, setNavRequestId] = useState(0);
	const [reactBuildReloadRequest, setReactBuildReloadRequest] = useState<{ buildId: string; reactBundle: ReactBundleModel; requestId: number }>();
	const handleReactBuildChanged = useCallback((buildId: string, reactBundle: ReactBundleModel) => {
		setReactBuildReloadRequest((current) => ({ buildId, reactBundle, requestId: (current?.requestId ?? 0) + 1 }));
	}, []);

	// Navigation state for the popover and navigation command

	// Track current URL from the runtime iframe (updated via VIEW_ACTIVATED messages)
	const [currentRuntimeUrl, setCurrentRuntimeUrl] = useState<string | null>(null);

	// Navigation state for "Navigate To" feature
	const [navPopoverAnchor, setNavPopoverAnchor] = useState<HTMLElement | null>(null);
	const [navPath, setNavPath] = useState('');
	const [navigationCommand, setNavigationCommand] = useState<string | null>(null);

	// Save menu state
	const [saveMenuAnchor, setSaveMenuAnchor] = useState<HTMLElement | null>(null);
	const saveMenuOpen = Boolean(saveMenuAnchor);

	const handleOpenNavPopover = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setNavPopoverAnchor(event.currentTarget);
		setNavPath('/');
	}, []);

	const handleCloseNavPopover = useCallback(() => {
		setNavPopoverAnchor(null);
		setNavPath('');
	}, []);

	const handleNavigateTo = useCallback(() => {
		// Extract path from full URL if provided (e.g., http://localhost:3001/transaction/123 -> /transaction/123)
		let path = navPath.trim();

		// Try to parse as URL to extract pathname
		if (path.includes('://')) {
			try {
				const url = new URL(path);
				path = url.pathname + url.search + url.hash;
			} catch {
				// If URL parsing fails, treat as a path
			}
		}

		// Ensure path starts with /
		if (!path.startsWith('/')) {
			path = '/' + path;
		}

		setNavigationCommand(path);
		handleCloseNavPopover();
	}, [navPath, handleCloseNavPopover]);

	const handleOpenSaveMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setSaveMenuAnchor(event.currentTarget);
	}, []);

	const handleCloseSaveMenu = useCallback(() => {
		setSaveMenuAnchor(null);
	}, []);

	const handleCopyBundleDefinition = useCallback(() => {
		if (bundleWithName) {
			void copyToClipboard(bundleWithName, (value) => serializeWithTypeFirst(value, 2));
		}
		handleCloseSaveMenu();
	}, [bundleWithName, copyToClipboard, handleCloseSaveMenu]);

	// Determine effective changeSetId (prioritize global selection, then record)
	const effectiveChangeSetId = useMemo(() => {
		return isSnapshotView ? record?.changeSetId?.trim() || '' : currentChangeSetId?.trim() || record?.changeSetId?.trim() || '';
	}, [currentChangeSetId, isSnapshotView, record]);

	const designerViewers = useAppDesignerPresence(
		record ? definitionId : undefined,
		isSnapshotView ? `version:${versionId}` : `changeset:${effectiveChangeSetId}`,
		selectedItem,
		selectedSharedComponentContext?.sharedComponentId,
	);
	const selectedArtifactViewers = selectedItem ? viewersForItem(designerViewers, selectedItem.type, selectedItem.id) : [];

	const { data: reactSourceFiles = [], isFetched: reactSourceFilesFetched } = useQuery<ReactSourceFileArtifact[]>({
		queryKey: ['userinterface', 'react-source-files', definitionId, effectiveChangeSetId, bundle?.ReactBundle?.BuildId],
		queryFn: () => client.getReactSourceFiles(definitionId!, effectiveChangeSetId || undefined),
		enabled: !!definitionId,
		staleTime: Infinity,
	});
	const reactCodeStatusQueryKey = useMemo(
		() => ['userinterface', 'react-code-status', definitionId, effectiveChangeSetId],
		[definitionId, effectiveChangeSetId],
	);
	const { data: reactCodeStatus } = useQuery({
		queryKey: reactCodeStatusQueryKey,
		queryFn: () => client.getReactCodeStatus(definitionId!, effectiveChangeSetId || undefined),
		enabled: !!definitionId,
	});
	useEffect(() => subscribeToReactBuilds(), [subscribeToReactBuilds]);
	useEffect(() => {
		if (latestReactBuildEvent?.payload.buildId !== reactCodeStatus?.buildId) return;

		void queryClient.invalidateQueries({ queryKey: reactCodeStatusQueryKey });
	}, [latestReactBuildEvent, queryClient, reactCodeStatus?.buildId, reactCodeStatusQueryKey]);
	const previousReactCodeStateRef = useRef<ReactCodeLifecycleState | undefined>(undefined);
	const [reactCodeOperationPending, setReactCodeOperationPending] = useState(false);
	const showReactCodeFailure = useCallback(
		(status: ReactCodeStatusModel) => {
			const detail = status.diagnostics?.find((diagnostic) => diagnostic.trim().length > 0);
			toastService.error(detail ? `React compile failed: ${detail}` : 'React compile failed');
		},
		[toastService],
	);
	useEffect(() => {
		const previousState = previousReactCodeStateRef.current;
		if (previousState === ReactCodeLifecycleState.Compiling && reactCodeStatus?.state === ReactCodeLifecycleState.Compiled) {
			toastService.success('React code compiled');
		} else if (previousState === ReactCodeLifecycleState.Compiling && reactCodeStatus?.state === ReactCodeLifecycleState.Failed) {
			showReactCodeFailure(reactCodeStatus);
		}
		previousReactCodeStateRef.current = reactCodeStatus?.state;
	}, [reactCodeStatus, showReactCodeFailure, toastService]);
	const handleCompileReactCode = useCallback(async () => {
		if (!definitionId || isSnapshotView) return;

		setReactCodeOperationPending(true);
		try {
			const status = await client.compileReactCode(definitionId, effectiveChangeSetId || undefined);
			queryClient.setQueryData(reactCodeStatusQueryKey, status);
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['userinterface', 'react-source-files', definitionId] }),
				queryClient.invalidateQueries({ queryKey: ['userinterface', 'react-source-file', definitionId] }),
			]);
			if (status.state === ReactCodeLifecycleState.Failed) {
				showReactCodeFailure(status);
			} else if (status.state === ReactCodeLifecycleState.Compiling) {
				toastService.success('React compilation queued');
			} else {
				toastService.success('React code compiled');
			}
		} catch (error) {
			toastService.error(error instanceof Error ? error.message : 'Failed to compile React code');
		} finally {
			setReactCodeOperationPending(false);
		}
	}, [client, definitionId, effectiveChangeSetId, isSnapshotView, queryClient, reactCodeStatusQueryKey, showReactCodeFailure, toastService]);
	const refreshPublishedReactBundle = useCallback(async (buildId: string | undefined) => {
		await queryClient.refetchQueries({ queryKey: bundleQueryKey, exact: true }, { throwOnError: true });
		const refreshedDefinition = queryClient.getQueryData<{ definition: string }>(bundleQueryKey);
		const refreshedBundle = refreshedDefinition ? JSON.parse(refreshedDefinition.definition) as Bundle : undefined;
		if (!buildId || refreshedBundle?.ReactBundle?.BuildId !== buildId) {
			throw new Error('React code was published, but the designer could not refresh the published build. Refresh the designer to retry.');
		}
		handleReactBuildChanged(buildId, refreshedBundle.ReactBundle);
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: ['userinterface', 'react-source-files', definitionId] }),
			queryClient.invalidateQueries({ queryKey: ['userinterface', 'react-source-file', definitionId] }),
		]);
	}, [bundleQueryKey, definitionId, handleReactBuildChanged, queryClient]);
	const buildPublishAbort = useRef<AbortController>();
	useEffect(() => () => buildPublishAbort.current?.abort(), [definitionId, effectiveChangeSetId]);
	const handleBuildAndPublishReactCode = useCallback(async () => {
		if (isSnapshotView) return;
		if (!definitionId || !effectiveChangeSetId) {
			toastService.error('Select a change set before building and publishing');
			return;
		}
		const controller = new AbortController();
		buildPublishAbort.current = controller;
		setReactCodeOperationPending(true);
		try {
			const status = await buildAndPublishReactCode(
				(buildId) => client.buildAndPublishReactCode(definitionId, effectiveChangeSetId, buildId, controller.signal),
				(status) => queryClient.setQueryData(reactCodeStatusQueryKey, status),
				controller.signal,
			);
			if (status.state === ReactCodeLifecycleState.Failed) {
				showReactCodeFailure(status);
				return;
			}
			await refreshPublishedReactBundle(status.buildId);
			toastService.success('React code built and published');
		} catch (error) {
			if (!controller.signal.aborted) {
				const validationError = parseValidationError(error);
				toastService.error(validationError ? getValidationErrorMessage(validationError) : error instanceof Error ? error.message : 'Build and publish failed');
			}
		} finally {
			setReactCodeOperationPending(false);
		}
	}, [client, definitionId, effectiveChangeSetId, isSnapshotView, queryClient, reactCodeStatusQueryKey, refreshPublishedReactBundle, showReactCodeFailure, toastService]);
	const handlePublishReactCode = useCallback(async () => {
		if (!definitionId || !effectiveChangeSetId || isSnapshotView) return;

		setReactCodeOperationPending(true);
		try {
			const status = await client.publishReactCode(definitionId, effectiveChangeSetId);
			queryClient.setQueryData(reactCodeStatusQueryKey, status);
			await refreshPublishedReactBundle(status.buildId);
			toastService.success('React code published');
		} catch (error) {
			const validationError = parseValidationError(error);
			toastService.error(validationError
				? getValidationErrorMessage(validationError)
				: error instanceof Error ? error.message : 'Failed to publish React code');
		} finally {
			setReactCodeOperationPending(false);
		}
	}, [client, definitionId, effectiveChangeSetId, isSnapshotView, queryClient, reactCodeStatusQueryKey, refreshPublishedReactBundle, toastService]);
	const [refreshPending, setRefreshPending] = useState(false);
	const handleRefreshDesigner = useCallback(async () => {
		if (!definitionId) return;

		setRefreshPending(true);
		try {
			await Promise.all([
				queryClient.refetchQueries({ queryKey: bundleQueryKey, exact: true }),
				queryClient.invalidateQueries({ queryKey: ['userinterface', 'react-source-files', definitionId] }),
				queryClient.invalidateQueries({ queryKey: ['userinterface', 'react-source-file', definitionId] }),
				queryClient.refetchQueries({ queryKey: reactCodeStatusQueryKey, exact: true }),
			]);
			toastService.success('App designer refreshed');
		} catch (error) {
			toastService.error(error instanceof Error ? error.message : 'Failed to refresh App designer');
		} finally {
			setRefreshPending(false);
		}
	}, [bundleQueryKey, definitionId, queryClient, reactCodeStatusQueryKey, toastService]);

	const reactSourceFileIds = useMemo(() => new Set(reactSourceFiles.map((file) => file.id)), [reactSourceFiles]);

	// Restore selectedItem from URL param on initial bundle load.
	// Only runs once: subsequent bundle changes (user edits, adding views/components) must NOT
	// re-read the URL because the URL update (persist effect) is deferred — so selectedItemFromQuery
	// would still hold the previous item's id and would revert the just-made selection.
	useEffect(() => {
		if (!bundle || initialSelectionResolved) return;
		const requestedId = initialSelectedItemIdRef.current;
		const requested = requestedId ? findItemById(bundle, reactSourceFileIds, requestedId) : null;
		if (requestedId && !requested && !reactSourceFilesFetched) return;
		const remembered = requested ?? (storedSelectedItem ? findItemById(bundle, reactSourceFileIds, storedSelectedItem.id) : null);
		const initialView = getInitialDesignerView(bundle.Views, remembered?.type === ITEM_TYPE_VIEW ? remembered.id : undefined);
		setSelectedItem(remembered && remembered.type !== ITEM_TYPE_VIEW ? remembered : initialView ? { type: ITEM_TYPE_VIEW, id: initialView.Id } : null);
		setInitialSelectionResolved(true);
	}, [bundle, initialSelectionResolved, reactSourceFileIds, reactSourceFilesFetched, storedSelectedItem, setSelectedItem]);

	// Persist selectedItem to query params (localStorage persistence is automatic via usePersistedSetting)
	useEffect(() => {
		if (!initialSelectionResolved) return;
		if (selectedItem) {
			// Only update URL if the value has changed to prevent navigation throttling
			if (selectedItemFromQuery !== selectedItem.id) {
				// Store only the ID in query params
				setSearchParam('selectedItem', selectedItem.id);
			}
		} else {
			// Only clear URL param if it's currently set
			if (selectedItemFromQuery !== null) {
				setSearchParam('selectedItem', undefined);
			}
		}
	}, [initialSelectionResolved, selectedItem, setSearchParam, selectedItemFromQuery]);

	useEffect(() => {
		if (record) {
			setBundleName(record.name);
			setBundleTags(record.tags ?? []);

			// Deserialize the bundle.definition JSON string into Bundle type
			try {
				const parsed = JSON.parse(record.definition) as Bundle;
				setBundle(withDefaultBundleSettings(parsed));
				setHasBundleChanges(false);
			} catch (error) {
				console.error('Failed to parse bundle definition:', error);
				toastService.error('Failed to parse bundle definition');
				setBundle(null);
				setHasBundleChanges(false);
			}
		}
	}, [record, toastService]);

	useEffect(() => {
		if (!isSnapshotView || !versionId || !bundle || snapshotSelectionAppliedRef.current === versionId) return;
		snapshotSelectionAppliedRef.current = versionId;
		const initialView = getInitialDesignerView(bundle.Views);
		setSelectedItem(initialView ? { type: ITEM_TYPE_VIEW, id: initialView.Id } : null);
		setInitialSelectionResolved(true);
	}, [bundle, isSnapshotView, setSelectedItem, versionId]);

	useEffect(() => {
		setHasBundleChanges((current) => current || (bundleWithName?.Name ?? '') !== (record?.name ?? ''));
	}, [bundleWithName?.Name, record?.name]);

	const selectedView = useMemo(
		() => (bundle && selectedItem?.type === ITEM_TYPE_VIEW ? (bundle.Views.find((v) => v.Id === selectedItem.id) ?? null) : null),
		[bundle, selectedItem],
	);

	const selectedComponent = useMemo(
		() => (bundle && selectedItem?.type === ITEM_TYPE_SHARED_COMPONENT ? (bundle.SharedComponents.find((c) => c.Id === selectedItem.id) ?? null) : null),
		[bundle, selectedItem],
	);

	const selectedResource = useMemo(
		() => (bundle && selectedItem?.type === ITEM_TYPE_EMBEDDED_RESOURCE ? (bundle.EmbeddedResources.find((r) => r.Id === selectedItem.id) ?? null) : null),
		[bundle, selectedItem],
	);

	const selectedTheme = useMemo(
		() => (bundle && selectedItem?.type === ITEM_TYPE_THEME ? (bundle.Themes?.find((theme) => theme.Id === selectedItem.id) ?? null) : null),
		[bundle, selectedItem],
	);
	const isThemeSelected = !!selectedTheme;
	const selectedReactSourceFile = useMemo(
		() => (selectedItem?.type === ITEM_TYPE_REACT_SOURCE_FILE ? (reactSourceFiles.find((file) => file.id === selectedItem.id) ?? null) : null),
		[reactSourceFiles, selectedItem],
	);
	const screenComponent = getSelectedComponentArtifactContext(bundle, selectedItem, selectedComponentPath, selectedSharedComponentContext, reactCodeStatus?.components);
	useDesignerScreenContext({
		ScreenArea: 'App', ScreenName: bundleName, AppDefinitionId: definitionId, AppVersionId: versionId,
		ScreenReadOnly: String(isSnapshotView), ScreenSelectionType: selectedItem?.type,
		ScreenSelectionName: selectedView?.Title ?? selectedComponent?.Name ?? selectedResource?.Name ?? selectedTheme?.Name ?? selectedReactSourceFile?.path,
		ViewId: selectedView?.Id, SharedComponentId: selectedComponent?.Id, ResourceId: selectedResource?.Id,
		ThemeId: selectedTheme?.Id, ThemeName: selectedTheme?.Name,
		ComponentId: screenComponent.componentId, ComponentPath: screenComponent.componentPath,
		ReactComponentId: screenComponent.reactComponentId, ReactComponentName: screenComponent.reactComponentName,
		ReactComponentHostState: screenComponent.reactComponentHostState,
		ReactSourceFileId: selectedReactSourceFile?.id, ReactSourceFilePath: selectedReactSourceFile?.path,
	});
	const { data: selectedReactSourceContent, isLoading: isReactSourceLoading } = useQuery({
		queryKey: ['userinterface', 'react-source-file', definitionId, effectiveChangeSetId, selectedReactSourceFile?.id],
		queryFn: () => client.getReactSourceFile(definitionId!, selectedReactSourceFile!.id, effectiveChangeSetId || undefined),
		enabled: !!definitionId && !!selectedReactSourceFile,
		staleTime: Infinity,
	});
	const [isReactSourceSaving, setIsReactSourceSaving] = useState(false);
	const handleSaveReactSource = useCallback(
		async (content: string) => {
			if (!definitionId || !selectedReactSourceFile || isSnapshotView) return;

			setIsReactSourceSaving(true);
			try {
				const updated = await client.updateReactSourceFile(
					definitionId,
					selectedReactSourceFile.id,
					effectiveChangeSetId || undefined,
					{ content },
				);
				queryClient.setQueryData(
					['userinterface', 'react-source-file', definitionId, effectiveChangeSetId, updated.id],
					updated,
				);
				await queryClient.invalidateQueries({ queryKey: ['userinterface', 'react-source-files', definitionId] });
				await queryClient.invalidateQueries({ queryKey: reactCodeStatusQueryKey, exact: true });
				if (updated.id !== selectedReactSourceFile.id) {
					setSelectedItem({ type: ITEM_TYPE_REACT_SOURCE_FILE, id: updated.id });
				}
				toastService.success('React source saved');
			} catch (error) {
				toastService.error(error instanceof Error ? error.message : 'Failed to save React source');
				throw error;
			} finally {
				setIsReactSourceSaving(false);
			}
		},
		[client, definitionId, effectiveChangeSetId, isSnapshotView, queryClient, reactCodeStatusQueryKey, selectedReactSourceFile, setSelectedItem, toastService],
	);

	const refreshReactSources = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: ['userinterface', 'react-source-files', definitionId] });
		await queryClient.invalidateQueries({ queryKey: reactCodeStatusQueryKey, exact: true });
	}, [definitionId, queryClient, reactCodeStatusQueryKey]);

	const handleRenameReactSourceFile = useCallback(async (sourceFileId: string, path: string) => {
		if (!definitionId || isSnapshotView) return;
		try {
			const renamed = await client.renameReactSourceFile(definitionId, sourceFileId, effectiveChangeSetId || undefined, { path });
			queryClient.setQueryData(['userinterface', 'react-source-file', definitionId, effectiveChangeSetId, renamed.id], renamed);
			await refreshReactSources();
			if (selectedItem?.type === ITEM_TYPE_REACT_SOURCE_FILE && selectedItem.id === sourceFileId) {
				setSelectedItem({ type: ITEM_TYPE_REACT_SOURCE_FILE, id: renamed.id });
			}
			toastService.success('React source renamed');
		} catch (error) {
			toastService.error(error instanceof Error ? error.message : 'Failed to rename React source');
		}
	}, [client, definitionId, effectiveChangeSetId, isSnapshotView, queryClient, refreshReactSources, selectedItem, setSelectedItem, toastService]);

	const handleRenameReactSourceFolder = useCallback(async (folderPath: string, name: string) => {
		if (!definitionId || isSnapshotView) return;
		try {
			const result = await client.renameReactSourceFolder(definitionId, effectiveChangeSetId || undefined, { folderPath, name });
			const selectedRename = result.files.find((file) => file.previousId === selectedItem?.id);
			await refreshReactSources();
			if (selectedItem?.type === ITEM_TYPE_REACT_SOURCE_FILE && selectedRename) {
				setSelectedItem({ type: ITEM_TYPE_REACT_SOURCE_FILE, id: selectedRename.id });
			}
			toastService.success('React source folder renamed');
		} catch (error) {
			toastService.error(error instanceof Error ? error.message : 'Failed to rename React source folder');
		}
	}, [client, definitionId, effectiveChangeSetId, isSnapshotView, refreshReactSources, selectedItem, setSelectedItem, toastService]);

	const handleCreateReactSourceFile = useCallback(async (path: string) => {
		if (!definitionId || isSnapshotView) return;
		try {
			const created = await client.createReactSourceFile(definitionId, effectiveChangeSetId || undefined, { path, content: '' });
			queryClient.setQueryData(['userinterface', 'react-source-file', definitionId, effectiveChangeSetId, created.id], created);
			await refreshReactSources();
			setSelectedItem({ type: ITEM_TYPE_REACT_SOURCE_FILE, id: created.id });
			toastService.success('React source file created');
		} catch (error) {
			toastService.error(error instanceof Error ? error.message : 'Failed to create React source file');
		}
	}, [client, definitionId, effectiveChangeSetId, isSnapshotView, queryClient, refreshReactSources, setSelectedItem, toastService]);

	const handleCopyViewDefinition = useCallback(() => {
		if (selectedView) {
			void copyToClipboard(selectedView, (value) => serializeWithTypeFirst(value, 2));
		}
		handleCloseSaveMenu();
	}, [selectedView, copyToClipboard, handleCloseSaveMenu]);

	const handlePasteViewDefinition = useCallback(async () => {
		if (!bundle || !selectedView) {
			return;
		}

		try {
			const clipboardText = await navigator.clipboard.readText();
			const pastedDefinition = JSON.parse(clipboardText) as unknown;
			if (!isViewDefinition(pastedDefinition)) {
				toastService.error('Clipboard does not contain a valid view definition.');
				return;
			}

			const replacement: ViewModel = { ...pastedDefinition, Id: selectedView.Id };
			handleBundleChanged({ ...bundle, Views: bundle.Views.map((view) => (view.Id === selectedView.Id ? replacement : view)) });
			setSelectedComponentPath(null);
			setSelectedSharedComponentContext(null);
			toastService.success('View definition replaced. Save the bundle to persist it.');
		} catch (error) {
			if (error instanceof SyntaxError) {
				toastService.error('Clipboard does not contain valid JSON.');
			} else {
				toastService.error('Failed to read the view definition from the clipboard.');
			}
		} finally {
			handleCloseSaveMenu();
		}
	}, [bundle, selectedView, toastService, handleBundleChanged, handleCloseSaveMenu]);

	// Compute a single view model for the preview frame.
	// This memo remains truthy for both views and shared components so the
	// RuntimePreviewFrame (and its iframe) stays mounted when switching between them.
	const viewForPreview = useMemo<IContainerComponentModel | null>(() => {
		if (selectedView) {
			return selectedView as IContainerComponentModel;
		}
		if (selectedComponent) {
			return {
				Id: selectedComponent.Id,
				Type: SHARED_COMPONENT_ROOT_TYPE,
				Children: [selectedComponent.Component],
				Layout: {},
			} as IContainerComponentModel;
		}
		return null;
	}, [selectedView, selectedComponent]);

	const handleUpdateComponentInView = useCallback(
		(updated: IComponentModel) => {
			if (!selectedView || !bundle || !selectedComponentPath) return;
			const updatedView = updateComponentAtPath(selectedView, selectedComponentPath, updated);
			handleBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === selectedView.Id ? updatedView : v)) });
		},
		[selectedView, bundle, selectedComponentPath, handleBundleChanged],
	);

	const handleUpdateComponentInSharedComponent = useCallback(
		(updated: IComponentModel) => {
			if (!selectedComponent || !bundle || !selectedComponentPath) return;

			// If the path points to the root component itself, replace it directly
			if (selectedComponentPath.length === 1 && selectedComponentPath[0] === selectedComponent.Component.Id) {
				const updatedSharedComponent = { ...selectedComponent, Component: updated as IContainerComponentModel };
				handleBundleChanged({ ...bundle, SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedComponent.Id ? updatedSharedComponent : c)) });
				return;
			}

			// For nested components, the path includes the root component ID as the first element
			// Strip it off to get the path relative to the root component's children
			const relativePath = selectedComponentPath[0] === selectedComponent.Component.Id ? selectedComponentPath.slice(1) : selectedComponentPath;

			const container = selectedComponent.Component as IContainerComponentModel;
			if (!container.Children) return;

			const updatedComponent = updateComponentAtPath(container, relativePath, updated);
			const updatedSharedComponent = { ...selectedComponent, Component: updatedComponent };
			handleBundleChanged({ ...bundle, SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedComponent.Id ? updatedSharedComponent : c)) });
		},
		[selectedComponent, bundle, selectedComponentPath, handleBundleChanged],
	);

	// Unified handlers that work with both Views and SharedComponents
	const handleComponentChanged = useCallback(
		(updated: IComponentModel) => {
			if (selectedItem?.type === ITEM_TYPE_VIEW && selectedSharedComponentContext && selectedComponentPath && bundle) {
				const resolution = resolveSharedComponentSelection(bundle, selectedComponentPath, selectedSharedComponentContext);
				if (!resolution) return;

				const updatedComponent =
					resolution.relativePath.length === 0
						? (updated as IContainerComponentModel)
						: updateComponentAtPath(resolution.root, resolution.relativePath, updated);

				handleBundleChanged({
					...bundle,
					SharedComponents: bundle.SharedComponents.map((c) =>
						c.Id === resolution.sharedComponent.Id ? { ...resolution.sharedComponent, Component: updatedComponent } : c,
					),
				});
			} else if (selectedItem?.type === ITEM_TYPE_VIEW) {
				handleUpdateComponentInView(updated);
			} else if (selectedItem?.type === ITEM_TYPE_SHARED_COMPONENT) {
				handleUpdateComponentInSharedComponent(updated);
			}
		},
		[
			selectedItem,
			selectedSharedComponentContext,
			selectedComponentPath,
			bundle,
			handleBundleChanged,
			handleUpdateComponentInView,
			handleUpdateComponentInSharedComponent,
		],
	);

	const handleAddComponent = useCallback(
		(metadata: ComponentMetadataModel) => {
			if (!bundle) return;

			if (selectedItem?.type === ITEM_TYPE_VIEW && selectedSharedComponentContext) {
				const resolution = resolveSharedComponentSelection(bundle, selectedComponentPath, selectedSharedComponentContext);
				if (!resolution) return;

				const selectedTreePathInSharedComponent = getComponentTreePath(resolution.pathInSharedComponent);
				const relativePath =
					selectedTreePathInSharedComponent?.[0] === resolution.root.Id
						? selectedTreePathInSharedComponent.slice(1)
						: (selectedTreePathInSharedComponent ?? []);
				const { containerPath } = findClosestContainerPath(resolution.root, relativePath, componentByName);
				const newComponent = createDefaultComponent(metadata);
				const updatedComponent = addComponentAtPath(resolution.root, containerPath, newComponent);
				const updatedSharedComponent = { ...resolution.sharedComponent, Component: updatedComponent };
				handleBundleChanged({
					...bundle,
					SharedComponents: bundle.SharedComponents.map((c) => (c.Id === resolution.sharedComponent.Id ? updatedSharedComponent : c)),
				});
				return;
			}

			const selectedTreePath = getComponentTreePath(selectedComponentPath);

			// If no component is selected, add to the root
			if (!selectedTreePath) {
				if (selectedItem?.type === ITEM_TYPE_VIEW && selectedView) {
					const newComponent = createDefaultComponent(metadata);
					const updatedView = addComponentAtPath(selectedView, [], newComponent);
					handleBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === selectedView.Id ? updatedView : v)) });
				} else if (selectedItem?.type === ITEM_TYPE_SHARED_COMPONENT && selectedComponent) {
					const newComponent = createDefaultComponent(metadata);
					const container = { ...selectedComponent.Component } as IContainerComponentModel;
					const updatedComponent = addComponentAtPath(container, [], newComponent);
					const updatedSharedComponent = { ...selectedComponent, Component: updatedComponent };
					handleBundleChanged({
						...bundle,
						SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedComponent.Id ? updatedSharedComponent : c)),
					});
				}
				return;
			}

			// Find the closest parent container
			if (selectedItem?.type === ITEM_TYPE_VIEW && selectedView) {
				const { containerPath } = findClosestContainerPath(selectedView, selectedTreePath, componentByName);
				const newComponent = createDefaultComponent(metadata);
				const updatedView = addComponentAtPath(selectedView, containerPath, newComponent);
				handleBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === selectedView.Id ? updatedView : v)) });
			} else if (selectedItem?.type === ITEM_TYPE_SHARED_COMPONENT && selectedComponent) {
				const container = { ...selectedComponent.Component } as IContainerComponentModel;

				// For shared components, the path includes the root component ID as the first element
				// We need to strip it because we're searching within container.Children
				// If selecting the root component itself (path length 1), use empty path
				const relativePath = selectedTreePath[0] === container.Id ? selectedTreePath.slice(1) : selectedTreePath;

				// If relative path is empty, we're adding to the root container
				if (relativePath.length === 0) {
					const componentMetadata = componentByName.get(container.Type);
					if (componentMetadata?.isContainer) {
						const newComponent = createDefaultComponent(metadata);
						const updatedComponent = addComponentAtPath(container, [], newComponent);
						const updatedSharedComponent = { ...selectedComponent, Component: updatedComponent };
						handleBundleChanged({
							...bundle,
							SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedComponent.Id ? updatedSharedComponent : c)),
						});
						return;
					}
				}

				// Find the closest container using the relative path
				const { containerPath } = findClosestContainerPath(container, relativePath, componentByName);
				const newComponent = createDefaultComponent(metadata);
				const updatedComponent = addComponentAtPath(container, containerPath, newComponent);
				const updatedSharedComponent = { ...selectedComponent, Component: updatedComponent };
				handleBundleChanged({ ...bundle, SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedComponent.Id ? updatedSharedComponent : c)) });
			}
		},
		[selectedItem, selectedSharedComponentContext, bundle, selectedComponentPath, selectedView, selectedComponent, componentByName, handleBundleChanged],
	);

	const handleMoveComponentUp = useCallback(() => {
		if (!bundle || !selectedComponentPath || selectedComponentPath.length === 0) return;

		if (selectedItem?.type === ITEM_TYPE_VIEW && selectedView) {
			const result = moveComponentUp(selectedView, selectedComponentPath);
			if (result) {
				handleBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === selectedView.Id ? result.updatedView : v)) });
				setSelectedComponentPath(result.newPath);
			}
		} else if (selectedItem?.type === ITEM_TYPE_SHARED_COMPONENT && selectedComponent) {
			const container = { ...selectedComponent.Component } as IContainerComponentModel;
			// Strip root component ID from path
			const relativePath = selectedComponentPath[0] === container.Id ? selectedComponentPath.slice(1) : selectedComponentPath;
			const result = moveComponentUp(container, relativePath);
			if (result) {
				const updatedSharedComponent = { ...selectedComponent, Component: result.updatedView };
				handleBundleChanged({ ...bundle, SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedComponent.Id ? updatedSharedComponent : c)) });
				// Restore root component ID to the returned path
				setSelectedComponentPath([container.Id, ...result.newPath]);
			}
		}
	}, [bundle, selectedComponentPath, selectedItem, selectedView, selectedComponent, handleBundleChanged]);

	const handleMoveComponentDown = useCallback(() => {
		if (!bundle || !selectedComponentPath || selectedComponentPath.length === 0) return;

		if (selectedItem?.type === ITEM_TYPE_VIEW && selectedView) {
			const result = moveComponentDown(selectedView, selectedComponentPath);
			if (result) {
				handleBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === selectedView.Id ? result.updatedView : v)) });
				setSelectedComponentPath(result.newPath);
			}
		} else if (selectedItem?.type === ITEM_TYPE_SHARED_COMPONENT && selectedComponent) {
			const container = { ...selectedComponent.Component } as IContainerComponentModel;
			// Strip root component ID from path
			const relativePath = selectedComponentPath[0] === container.Id ? selectedComponentPath.slice(1) : selectedComponentPath;
			const result = moveComponentDown(container, relativePath);
			if (result) {
				const updatedSharedComponent = { ...selectedComponent, Component: result.updatedView };
				handleBundleChanged({ ...bundle, SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedComponent.Id ? updatedSharedComponent : c)) });
				// Restore root component ID to the returned path
				setSelectedComponentPath([container.Id, ...result.newPath]);
			}
		}
	}, [bundle, selectedComponentPath, selectedItem, selectedView, selectedComponent, handleBundleChanged]);

	const handleRemoveComponent = useCallback(() => {
		if (!bundle || !selectedComponentPath || selectedComponentPath.length === 0) return;

		if (selectedItem?.type === ITEM_TYPE_VIEW && selectedSharedComponentContext) {
			const resolution = resolveSharedComponentSelection(bundle, selectedComponentPath, selectedSharedComponentContext);
			if (!resolution) {
				toastService.error('Could not locate selected component in shared component');
				return;
			}

			const updatedComponent = removeComponentAtPath(resolution.root, resolution.relativePath);
			if (updatedComponent) {
				handleBundleChanged({
					...bundle,
					SharedComponents: bundle.SharedComponents.map((c) =>
						c.Id === resolution.sharedComponent.Id ? { ...resolution.sharedComponent, Component: updatedComponent } : c,
					),
				});
				setSelectedComponentPath(null);
				setSelectedSharedComponentContext(null);
			} else {
				toastService.error('Could not locate selected component to delete');
			}
		} else if (selectedItem?.type === ITEM_TYPE_VIEW && selectedView) {
			const updatedView = removeComponentAtPath(selectedView, selectedComponentPath);
			if (updatedView) {
				handleBundleChanged({ ...bundle, Views: bundle.Views.map((v) => (v.Id === selectedView.Id ? updatedView : v)) });
				setSelectedComponentPath(null); // Clear selection after removal
			}
		} else if (selectedItem?.type === ITEM_TYPE_SHARED_COMPONENT && selectedComponent) {
			const container = { ...selectedComponent.Component } as IContainerComponentModel;
			// Strip root component ID from path
			const relativePath = selectedComponentPath[0] === container.Id ? selectedComponentPath.slice(1) : selectedComponentPath;
			const updatedComponent = removeComponentAtPath(container, relativePath);
			if (updatedComponent) {
				const updatedSharedComponent = { ...selectedComponent, Component: updatedComponent };
				handleBundleChanged({ ...bundle, SharedComponents: bundle.SharedComponents.map((c) => (c.Id === selectedComponent.Id ? updatedSharedComponent : c)) });
				setSelectedComponentPath(null); // Clear selection after removal
			}
		}
	}, [bundle, selectedComponentPath, selectedItem, selectedSharedComponentContext, selectedView, selectedComponent, handleBundleChanged, toastService]);

	const handleSave = useCallback(async () => {
		if (!definitionId || isSnapshotView) return;

		const definitionToSave = bundleWithName ? serializeWithTypeFirst(withDefaultBundleSettings(bundleWithName)) : (record?.definition ?? '');

		setSavePending(true);
		try {
			setSaveError(undefined);
			const refreshed = await client.update(record!.id, {
				changeSetId: effectiveChangeSetId,
				name: bundleName,
				tags: bundleTags,
				definition: definitionToSave,
			});
			queryClient.setQueryData(bundleQueryKey, refreshed);
			void queryClient.invalidateQueries({ queryKey: ['userinterface', 'bundles'] });

			try {
				const parsed = withDefaultBundleSettings(JSON.parse(refreshed.definition) as Bundle);
				setBundle(parsed);
				setHasBundleChanges(false);
			} catch {
				setHasBundleChanges(false);
			}
			toastService.success('Bundle saved');
		} catch (err) {
			let saveError: SaveError = {
				title: 'Failed to save app',
				detail: err instanceof Error && err.message ? err.message : 'An unexpected error occurred while saving the app.',
				validationIssues: [],
			};
			if (ApiException.isApiException(err)) {
				try {
					const body = JSON.parse(err.response) as { detail?: string; title?: string; traceId?: string; TraceId?: string };
					saveError = {
						title: body.title ?? saveError.title,
						detail: body.detail ?? body.title ?? saveError.detail,
						traceId: body.traceId ?? body.TraceId,
						validationIssues: [],
					};
				} catch {
					// Response wasn't JSON; use the generated client's error message.
				}
			}
			setSaveError(saveError);
		} finally {
			setSavePending(false);
		}
	}, [bundleQueryKey, definitionId, bundleWithName, bundleName, bundleTags, record, client, effectiveChangeSetId, isSnapshotView, queryClient, toastService]);

	const handleSelectItem = useCallback(
		(item: SelectedItem | null) => {
			setInitialSelectionResolved(true);
			setSelectedItem(item);
			setSelectedComponentPath(null);
			setSelectedSharedComponentContext(null);
			// Only views and shared components have a runtime preview — trigger navigation.
			if (item?.type === ITEM_TYPE_VIEW || item?.type === ITEM_TYPE_SHARED_COMPONENT) {
				setNavRequestId((n) => n + 1);
			}
		},
		[setSelectedItem],
	);

	const handleDeleteItem = useCallback(
		async (item: SelectedItem) => {
			if (isSnapshotView) return;
			if (item.type === ITEM_TYPE_REACT_SOURCE_FILE) {
				const source = reactSourceFiles.find(file => file.id === item.id);
				if (!definitionId || !source || isGeneratedReactSourceFile(source)) return;
				try {
					await client.deleteReactSourceFile(definitionId, item.id, effectiveChangeSetId || undefined);
				} catch (error) {
					toastService.error(error instanceof Error ? error.message : 'Failed to delete React source file');
					return;
				}
				const contentKey = ['userinterface', 'react-source-file', definitionId, effectiveChangeSetId, item.id];
				await queryClient.cancelQueries({ queryKey: contentKey, exact: true });
				queryClient.removeQueries({ queryKey: contentKey, exact: true });
				setSelectedItem(current => current?.type === item.type && current.id === item.id ? null : current);
				toastService.success('React source file deleted');
				try {
					await refreshReactSources();
				} catch {
					toastService.error('The file was deleted, but the source list could not refresh. Please refresh the App.');
				}
				return;
			}
			if (!bundle) return;
			const remainingThemes = item.type === ITEM_TYPE_THEME ? bundle.Themes!.filter((theme) => theme.Id !== item.id) : bundle.Themes;
			const defaultThemeId = remainingThemes?.find((theme) => theme.IsDefault)?.Id ?? remainingThemes?.[0]?.Id;

			const nextBundle: Bundle = {
				...bundle,
				SharedComponents:
					item.type === ITEM_TYPE_SHARED_COMPONENT ? bundle.SharedComponents.filter((component) => component.Id !== item.id) : bundle.SharedComponents,
				Views: item.type === ITEM_TYPE_VIEW ? bundle.Views.filter((view) => view.Id !== item.id) : bundle.Views,
				EmbeddedResources:
					item.type === ITEM_TYPE_EMBEDDED_RESOURCE ? bundle.EmbeddedResources.filter((resource) => resource.Id !== item.id) : bundle.EmbeddedResources,
				Themes:
					item.type === ITEM_TYPE_THEME
						? remainingThemes!.map((theme) => ({ ...theme, IsDefault: theme.Id === defaultThemeId }))
						: bundle.Themes,
			};

			handleBundleChanged(nextBundle);
			setSelectedItem((current) => (current?.type === item.type && current.id === item.id ? null : current));
		},
		[bundle, setSelectedItem, handleBundleChanged, client, definitionId, effectiveChangeSetId, isSnapshotView, queryClient, reactSourceFiles, refreshReactSources, toastService],
	);

	// Passive sync: the runtime navigated to a different view (user followed a link).
	// Updates Artifacts selection and clears stale component path — does NOT trigger a
	// navigation message back to the runtime (navRequestId is intentionally not touched).
	const handleNavigationToView = useCallback(
		(viewId: string) => {
			setInitialSelectionResolved(true);
			setSelectedItem((current) => {
				if (current?.id === viewId) return current;
				return { type: ITEM_TYPE_VIEW, id: viewId };
			});
			setSelectedComponentPath(null);
			setSelectedSharedComponentContext(null);
		},
		[setSelectedItem],
	);

	// Syncs selectedItem + selectedComponentPath atomically when the user ctrl-clicks a component
	// inside the runtime iframe. Both state updates are batched (React 18) so Properties always
	// sees a consistent view+path pair. navRequestId is NOT incremented — we stay on the current view.
	const handleSelectedComponentPathChange = useCallback(
		(path: ComponentPath | null, viewId?: string, sharedComponentId?: string, referenceComponentPath?: ComponentPath) => {
			setInitialSelectionResolved(true);
			// If clicking inside a ReferenceComponent that renders a SharedComponent,
			// keep the View selected but track the nested component selection
			if (sharedComponentId && referenceComponentPath !== undefined) {
				const sharedComp = bundle?.SharedComponents.find((sc) => sc.Id === sharedComponentId);
				if (!sharedComp) {
					console.warn('[BundleDesigner] SharedComponent not found:', sharedComponentId);
					return;
				}

				// Keep the View selected (don't switch to SharedComponent designer)
				if (viewId) {
					setSelectedItem((current) => {
						if (!current || current.id !== viewId) return { type: ITEM_TYPE_VIEW, id: viewId };
						return current;
					});
				}

				// Track the SharedComponent context so AppItemProperties can resolve the component
				setSelectedSharedComponentContext({
					sharedComponentId,
					referenceComponentPath,
				});

				// Use the full View-relative path (don't transform it)
				// The ReferenceComponent extends the path when rendering, so this will match for selection
				setSelectedComponentPath(path && path.length > 0 ? path : null);
				return;
			}

			// Normal View selection (not inside SharedComponent)
			setSelectedSharedComponentContext(null);

			if (viewId) {
				setSelectedItem((current) => {
					if (!current || current.id !== viewId) return { type: ITEM_TYPE_VIEW, id: viewId };
					return current;
				});
			}
			// Empty path means the view itself was clicked (deselect component)
			setSelectedComponentPath(path && path.length > 0 ? path : null);
		},
		[setSelectedItem, bundle],
	);

	if (isLoading) {
		return <ViewLoader />;
	}

	return (
		<DesignerAppProvider bundle={bundle}>
			<Stack direction="column" sx={{ flexGrow: 1, minHeight: 0 }}>
				<Box sx={(theme) => ({ px: `${theme.layout.edgeGap}px`, pt: `${theme.layout.edgeGap}px` })}>
					<ViewTitleBar
						title={isSnapshotView && record ? `${bundleName} — v${record.versionId}` : bundleName}
						subtitle={isSnapshotView ? 'Historical App snapshot · Saving disabled' : undefined}
						onTitleChanged={isSnapshotView ? undefined : setBundleName}
						titleAdornment={<>
							<DesignerPresenceIndicator viewers={designerViewers} label="Also in this App designer" showLabel />
							<DesignerPresenceIndicator viewers={selectedArtifactViewers} label="Viewing the same artifact" showLabel />
						</>}
					>
						{isSnapshotView ? (
							<Stack direction="row" spacing={1}>
								<Button component={Link} to={`/app/${definitionId}`} startIcon={<ArrowBackIcon />} variant="outlined">
									Back to App
								</Button>
								<Button variant="contained" startIcon={<RestoreIcon />} disabled={!record || !(currentChangeSetId || record.changeSetId)} onClick={() => setRestoreDialogOpen(true)}>
									Restore as latest
								</Button>
							</Stack>
						) : <Stack direction="row" spacing={1}>
							<Button component={Link} to={`/app/${definitionId}/resources`} disabled={hasBundleChanges} title={hasBundleChanges ? 'Save App changes before opening resources' : 'Manage App authoring resources'}>Resources</Button>
							<Button
								variant="outlined"
								startIcon={<RefreshIcon />}
								disabled={refreshPending || hasBundleChanges}
								loading={refreshPending}
								onClick={handleRefreshDesigner}
								title={hasBundleChanges ? 'Save App changes before refreshing' : 'Refresh Bundle and React code state'}
							>
								Refresh
							</Button>
							<ButtonGroup variant="contained" disabled={savePending}>
								<Button startIcon={<SendIcon />} disabled={savePending || !hasBundleChanges} loading={savePending} onClick={handleSave}>
									Save
								</Button>
								<Button size="small" onClick={handleOpenSaveMenu} disabled={savePending} sx={{ px: 0.5 }}>
									<ArrowDropDownIcon />
								</Button>
							</ButtonGroup>
						</Stack>}
					</ViewTitleBar>
				</Box>
				{isMobile && (
					<Tabs
						value={mobileDesignerSection}
						onChange={(_, value: 'artifacts' | 'preview' | 'properties' | 'chat') => setMobileDesignerSection(value)}
						variant="fullWidth"
						aria-label="App designer sections"
						sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minWidth: 0, minHeight: 48, px: 0.5, fontSize: '0.7rem' } }}
					>
						<Tab value="artifacts" icon={<FolderIcon fontSize="small" />} iconPosition="start" label="Artifacts" />
						<Tab value="preview" icon={<VisibilityIcon fontSize="small" />} iconPosition="start" label="Preview" />
						<Tab value="properties" icon={<TuneIcon fontSize="small" />} iconPosition="start" label="Properties" />
						{!isSnapshotView && <Tab value="chat" icon={<ChatIcon fontSize="small" />} iconPosition="start" label="Chat" />}
					</Tabs>
				)}
				<Stack
					direction={isMobile ? 'column' : 'row'}
					sx={(theme) => ({
						flexGrow: 1,
						minHeight: 0,
						overflow: 'hidden',
						gap: `${theme.layout.panelGap}px`,
						px: `${theme.layout.edgeGap}px`,
						pt: `${theme.layout.panelGap}px`,
						pb: `${theme.layout.edgeGap}px`,
					})}
				>
					<Box sx={{ display: !isMobile || mobileDesignerSection === 'artifacts' ? 'flex' : 'none', flex: isMobile ? 1 : 'none', flexShrink: 0, width: isMobile ? '100%' : undefined, minHeight: 0 }}>
						<AppArtifactPanel
							viewers={designerViewers}
							bundle={bundle}
							selectedItem={selectedItem}
							onSelectItem={handleSelectItem}
							onDeleteItem={handleDeleteItem}
							onBundleChanged={handleBundleChanged}
							reactSourceFiles={reactSourceFiles.map((file) => ({ id: file.id, path: file.path, tags: file.tags }))}
							reactCodeStatus={reactCodeStatus}
							reactCodeOperationPending={reactCodeOperationPending}
							onCompileReactCode={handleCompileReactCode}
							onPublishReactCode={handlePublishReactCode}
							onBuildAndPublishReactCode={handleBuildAndPublishReactCode}
							onRenameReactSourceFile={handleRenameReactSourceFile}
							onRenameReactSourceFolder={handleRenameReactSourceFolder}
							onCreateReactSourceFile={handleCreateReactSourceFile}
							isMobile={isMobile}
							readOnly={isSnapshotView}
						/>
					</Box>
					<Paper sx={{ display: !isMobile || mobileDesignerSection === 'preview' ? 'flex' : 'none', flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
						<DesignerViewProvider view={selectedView} sharedComponent={selectedComponent}>
							<Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
								{/* Single persistent preview frame — stays mounted when switching between views and
								    shared components so the app-runtime iframe never reloads. Navigation between
								    different item types is handled via postMessage to the React Router inside app-runtime. */}
								{viewForPreview && bundleWithName && (
									<RuntimePreviewFrame
										view={viewForPreview}
										navigationRequestId={navRequestId}
										selectedPath={selectedComponentPath}
										selectedSharedComponentContext={selectedSharedComponentContext}
										onSelectionChange={handleSelectedComponentPathChange}
										bundle={bundleWithName}
										currentRuntimeUrl={currentRuntimeUrl}
										onCurrentUrlChange={setCurrentRuntimeUrl}
										navigationCommand={navigationCommand}
										onNavigationToView={handleNavigationToView}
										title={selectedView?.Title ?? selectedComponent?.Name}
										routePath={selectedView?.RoutePath}
										onNavigateClick={handleOpenNavPopover}
										onCopyViewDefinition={selectedView ? handleCopyViewDefinition : undefined}
										onPasteViewDefinition={selectedView ? () => void handlePasteViewDefinition() : undefined}
										reactBuildReloadRequest={reactBuildReloadRequest}
									/>
								)}
								{selectedResource && bundle && (
									<AppResourceDesigner
										definitionId={definitionId!}
										changeSetId={effectiveChangeSetId || undefined}
										readOnly={isSnapshotView}
										model={selectedResource}
										setModel={(updated: EmbeddedResourceModel) =>
											handleBundleChanged({ ...bundle, EmbeddedResources: bundle.EmbeddedResources.map((r) => (r.Id === updated.Id ? updated : r)) })
										}
									/>
								)}
								{isThemeSelected && bundle && selectedTheme && (
									<ThemeDesigner
										theme={selectedTheme.Theme ?? {}}
										setTheme={(updated) =>
											handleBundleChanged({ ...bundle, Themes: bundle.Themes!.map((theme) => (theme.Id === selectedTheme.Id ? { ...theme, Theme: updated } : theme)) })
										}
										name={selectedTheme.Name}
										setName={(name) =>
											handleBundleChanged({ ...bundle, Themes: bundle.Themes!.map((theme) => (theme.Id === selectedTheme.Id ? { ...theme, Name: name } : theme)) })
										}
										isDefault={selectedTheme.IsDefault}
										setDefault={() =>
											handleBundleChanged({ ...bundle, Themes: bundle.Themes!.map((theme) => ({ ...theme, IsDefault: theme.Id === selectedTheme.Id })) })
										}
									/>
								)}
								{selectedReactSourceFile && (
									<CodeFileViewer
										onDirtyChange={setHasSourceChanges}
										path={selectedReactSourceFile.path}
										content={selectedReactSourceContent?.content}
										loading={isReactSourceLoading}
										editable={!isSnapshotView && !isGeneratedReactSourceFile(selectedReactSourceFile)}
										saving={isReactSourceSaving}
										onSave={handleSaveReactSource}
									/>
								)}
							</Box>
						</DesignerViewProvider>
					</Paper>
					<Box sx={{ display: !isMobile || mobileDesignerSection === 'properties' || mobileDesignerSection === 'chat' ? 'flex' : 'none', flex: isMobile ? 1 : 'none', width: isMobile ? '100%' : undefined, minWidth: 0, minHeight: 0 }}>
						<ActionPanel
							hasUnsavedChanges={hasBundleChanges || hasSourceChanges || savePending || isReactSourceSaving}
							definitionId={definitionId}
							bundleQueryKey={bundleQueryKey}
							selectedItem={selectedItem}
							bundle={bundle}
							reactComponents={reactCodeStatus?.components}
							selectedComponentPath={selectedComponentPath}
							selectedSharedComponentContext={selectedSharedComponentContext}
							onBundleChanged={handleBundleChanged}
							onComponentChanged={handleComponentChanged}
							onAddComponent={handleAddComponent}
							onMoveComponentUp={handleMoveComponentUp}
							onMoveComponentDown={handleMoveComponentDown}
							onRemoveComponent={handleRemoveComponent}
							onSelectedComponentPathChanged={setSelectedComponentPath}
							onReactBuildChanged={handleReactBuildChanged}
							selectedReactSourceFile={selectedReactSourceFile}
							changeSetId={effectiveChangeSetId}
							isMobile={isMobile}
							mobileChatMode={isMobile && mobileDesignerSection === 'chat'}
							readOnly={isSnapshotView}
						/>
					</Box>
				</Stack>
			</Stack>

			{/* Save Menu */}
			{!isSnapshotView && <Menu
				anchorEl={saveMenuAnchor}
				open={saveMenuOpen}
				onClose={handleCloseSaveMenu}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				<MenuItem onClick={() => {
					setVersionTagsText(bundleTags.join('\n'));
					setVersionTagsDialogOpen(true);
					handleCloseSaveMenu();
				}}>
					<ListItemIcon><LabelIcon fontSize="small" /></ListItemIcon>
					<ListItemText>Version tags</ListItemText>
				</MenuItem>
				<MenuItem onClick={handleCopyBundleDefinition} disabled={!bundle}>
					<ListItemIcon>
						<ContentCopyIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Copy Bundle Definition</ListItemText>
				</MenuItem>
			</Menu>}

			{/* Navigate To Popover */}
			<Popover
				open={Boolean(navPopoverAnchor)}
				anchorEl={navPopoverAnchor}
				onClose={handleCloseNavPopover}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				<Box sx={{ p: 2, width: 400 }}>
					<Stack spacing={2}>
						<Typography variant="subtitle2">Navigate To</Typography>
						<TextField
							autoFocus
							fullWidth
							size="small"
							label="URL or Path"
							placeholder="/your-path"
							value={navPath}
							onChange={(e) => setNavPath(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									handleNavigateTo();
								}
							}}
						/>
						<Stack direction="row" spacing={1} justifyContent="flex-end">
							<Button size="small" onClick={handleCloseNavPopover}>
								Cancel
							</Button>
							<Button size="small" variant="contained" onClick={handleNavigateTo} disabled={!navPath}>
								Navigate
							</Button>
						</Stack>
					</Stack>
				</Box>
			</Popover>

			<RestoreAppVersionDialog
				version={restoreDialogOpen && isSnapshotView ? record ?? null : null}
				changeSetId={currentChangeSetId || record?.changeSetId}
				onClose={() => setRestoreDialogOpen(false)}
				onRestored={() => navigate(`/app/${definitionId}`)}
			/>
			<ConfirmDialog
				open={versionTagsDialogOpen && !isSnapshotView}
				title="Version Tags"
				message="These tags will be stored on the next saved App version. Apply the tags, then Save the App to create that version."
				confirmText="Apply"
				onConfirm={() => {
					setBundleTags(parseVersionTags(versionTagsText));
					setHasBundleChanges(true);
					setVersionTagsDialogOpen(false);
				}}
				onCancel={() => setVersionTagsDialogOpen(false)}
			>
				<TextField
					label="Version tags"
					value={versionTagsText}
					onChange={(event) => setVersionTagsText(event.target.value)}
					multiline
					minRows={3}
					fullWidth
					helperText="One tag per line."
					sx={{ mt: 2 }}
				/>
			</ConfirmDialog>
			<SaveErrorDialog error={saveError} onClose={() => setSaveError(undefined)} />
		</DesignerAppProvider>
	);
}

export default function AppDesignerView() {
	return (
		<UserInterfaceMetadataProvider>
			<AppDesignerContent />
		</UserInterfaceMetadataProvider>
	);
}
